import { addDays, addYears, differenceInCalendarDays, parseISO, startOfDay } from 'date-fns';

export type HomePropertyMatchStatus = 'not_checked' | 'matched' | 'ambiguous' | 'unavailable' | 'failed';
export type HomePropertyConfidence = 'high' | 'medium' | 'low';
export type HomePropertyValueOrigin = 'user_entered' | 'source_enriched' | 'user_confirmed_override';

export type HomePropertyLookupCandidate = {
  address: string;
  postcode: string | null;
  uprn: string | null;
};

export type HomeBinCollectionEntry = {
  service: string;
  nextCollectionDate: string | null;
  scheduleLabel: string | null;
  containers: string[];
};

export type HomePropertyEnrichmentResult = {
  normalisedAddress: string | null;
  postcode: string | null;
  uprn: string | null;
  candidates: HomePropertyLookupCandidate[];
  checkedAt: string | null;
  note: string | null;
  epc: {
    status: HomePropertyMatchStatus;
    rating: string | null;
    score: number | null;
    expiryDate: string | null;
    source: string | null;
    sourceReference: string | null;
    confidence: HomePropertyConfidence | null;
  };
  councilTax: {
    status: HomePropertyMatchStatus;
    band: string | null;
    annualCharge: number | null;
    authorityName: string | null;
    source: string | null;
    sourceReference: string | null;
    confidence: HomePropertyConfidence | null;
  };
  bins: {
    status: HomePropertyMatchStatus;
    weekLabel: string | null;
    source: string | null;
    sourceReference: string | null;
    confidence: HomePropertyConfidence | null;
    entries: HomeBinCollectionEntry[];
    calendarUrl: string | null;
    gardenCalendarUrl: string | null;
  };
};

export type HomePropertyLookupInput = {
  lookupQuery: string | null;
  normalizedAddress: string | null;
  postcode: string | null;
  canLookup: boolean;
  reason: string | null;
};

export type HomePropertyLookupCandidateRow = {
  address: string;
  postcode: string | null;
  uprn: string | null;
  rating: string | null;
  score: number | null;
  expiryDate: string | null;
  sourceReference: string | null;
};

const UK_POSTCODE_PATTERN = /\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i;

function collapseWhitespace(value: string) {
  return value.replace(/\s+/g, ' ').trim();
}

function normalizeAddressForComparison(value: string | null | undefined) {
  return collapseWhitespace(String(value || ''))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeUkPostcode(value: string | null | undefined) {
  const trimmed = collapseWhitespace(String(value || '').toUpperCase());
  const compact = trimmed.replace(/\s+/g, '');
  if (compact.length < 5) return null;
  return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
}

export function extractUkPostcode(value: string | null | undefined) {
  const match = String(value || '').toUpperCase().match(UK_POSTCODE_PATTERN);
  return match ? normalizeUkPostcode(match[1]) : null;
}

export function normalizeHomePropertyLookupInput(address: string | null | undefined): HomePropertyLookupInput {
  const trimmedAddress = collapseWhitespace(String(address || ''));
  const postcode = extractUkPostcode(trimmedAddress);
  const lookupQuery = collapseWhitespace(
    postcode ? trimmedAddress.replace(new RegExp(postcode.replace(/\s+/g, '\\s*'), 'i'), postcode) : trimmedAddress,
  );
  const normalizedAddress = normalizeAddressForComparison(lookupQuery);

  if (!lookupQuery) {
    return {
      lookupQuery: null,
      normalizedAddress: null,
      postcode: null,
      canLookup: false,
      reason: 'Add the address before checking property details.',
    };
  }

  if (!postcode) {
    return {
      lookupQuery,
      normalizedAddress,
      postcode: null,
      canLookup: false,
      reason: 'Add a full postcode so Adulting can check the right property.',
    };
  }

  const addressWithoutPostcode = collapseWhitespace(lookupQuery.replace(new RegExp(postcode.replace(/\s+/g, '\\s*'), 'i'), ''));
  if (addressWithoutPostcode.length < 4) {
    return {
      lookupQuery,
      normalizedAddress,
      postcode,
      canLookup: false,
      reason: 'Add a little more address detail so Adulting can narrow the lookup.',
    };
  }

  return {
    lookupQuery,
    normalizedAddress,
    postcode,
    canLookup: true,
    reason: null,
  };
}

export function buildUnavailablePropertyEnrichment(
  input: HomePropertyLookupInput,
  note: string,
): HomePropertyEnrichmentResult {
  return {
    normalisedAddress: input.lookupQuery,
    postcode: input.postcode,
    uprn: null,
    candidates: [],
    checkedAt: null,
    note,
    epc: {
      status: input.canLookup ? 'unavailable' : 'not_checked',
      rating: null,
      score: null,
      expiryDate: null,
      source: null,
      sourceReference: null,
      confidence: null,
    },
    councilTax: {
      status: input.canLookup ? 'unavailable' : 'not_checked',
      band: null,
      annualCharge: null,
      authorityName: null,
      source: null,
      sourceReference: null,
      confidence: null,
    },
    bins: {
      status: input.canLookup ? 'unavailable' : 'not_checked',
      weekLabel: null,
      source: null,
      sourceReference: null,
      confidence: null,
      entries: [],
      calendarUrl: null,
      gardenCalendarUrl: null,
    },
  };
}

export function selectBestPropertyLookupMatch(
  rows: HomePropertyLookupCandidateRow[],
  input: HomePropertyLookupInput,
) {
  const normalizedPostcode = normalizeUkPostcode(input.postcode);
  const uniqueRows = rows.filter((row, index) => {
    const key = `${normalizeAddressForComparison(row.address)}|${normalizeUkPostcode(row.postcode) || ''}|${row.uprn || ''}`;
    return rows.findIndex((candidate) => (
      `${normalizeAddressForComparison(candidate.address)}|${normalizeUkPostcode(candidate.postcode) || ''}|${candidate.uprn || ''}`
    ) === key) === index;
  });

  const exactMatch = uniqueRows.find((row) => {
    const rowPostcode = normalizeUkPostcode(row.postcode);
    const rowAddress = normalizeAddressForComparison(
      rowPostcode ? `${row.address} ${rowPostcode}` : row.address,
    );
    return rowAddress === input.normalizedAddress;
  });

  if (exactMatch) {
    return {
      status: 'matched' as const,
      confidence: 'high' as const,
      match: exactMatch,
      candidates: [] as HomePropertyLookupCandidate[],
    };
  }

  const postcodeRows = uniqueRows.filter((row) => normalizeUkPostcode(row.postcode) === normalizedPostcode);
  if (postcodeRows.length === 1) {
    return {
      status: 'matched' as const,
      confidence: 'medium' as const,
      match: postcodeRows[0],
      candidates: [] as HomePropertyLookupCandidate[],
    };
  }

  if (postcodeRows.length > 1) {
    return {
      status: 'ambiguous' as const,
      confidence: 'low' as const,
      match: null,
      candidates: postcodeRows.slice(0, 3).map((row) => ({
        address: row.address,
        postcode: row.postcode,
        uprn: row.uprn,
      })),
    };
  }

  if (uniqueRows.length === 1) {
    return {
      status: 'matched' as const,
      confidence: 'medium' as const,
      match: uniqueRows[0],
      candidates: [] as HomePropertyLookupCandidate[],
    };
  }

  return {
    status: uniqueRows.length ? ('ambiguous' as const) : ('unavailable' as const),
    confidence: uniqueRows.length ? ('low' as const) : null,
    match: null,
    candidates: uniqueRows.slice(0, 3).map((row) => ({
      address: row.address,
      postcode: row.postcode,
      uprn: row.uprn,
    })),
  };
}

export function resolveHomePropertyValueOrigin(input: {
  value: string | null;
  previousOrigin: HomePropertyValueOrigin | null;
  lookupValue: string | null;
  lookupMatched: boolean;
  manualOverride: boolean;
}) {
  if (!input.value) return null;
  if (input.manualOverride) {
    return input.previousOrigin === 'source_enriched' || input.lookupValue
      ? 'user_confirmed_override'
      : 'user_entered';
  }

  if (input.lookupMatched && input.lookupValue && input.lookupValue === input.value) {
    return 'source_enriched';
  }

  return input.previousOrigin || 'user_entered';
}

export function formatHomePropertyValueOrigin(origin: HomePropertyValueOrigin | null | undefined) {
  if (origin === 'source_enriched') return 'Pulled from property lookup';
  if (origin === 'user_confirmed_override') return 'Entered by you';
  if (origin === 'user_entered') return 'Entered by you';
  return null;
}

export function isLowEpcRating(value: string | null | undefined) {
  return ['E', 'F', 'G'].includes(String(value || '').toUpperCase());
}

export function isEpcExpiryWithinWindow(value: string | null | undefined, days = 90) {
  if (!value) return false;
  const difference = differenceInCalendarDays(parseISO(value), startOfDay(new Date()));
  return difference >= 0 && difference <= days;
}

export function deriveEpcExpiryDateFromLodgement(value: string | null | undefined) {
  if (!value) return null;
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return null;
  return addYears(date, 10).toISOString().slice(0, 10);
}

export function buildPropertyCheckedDateLabel(value: string | null | undefined) {
  if (!value) return null;
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return null;
  const daysAgo = differenceInCalendarDays(startOfDay(new Date()), startOfDay(date));
  if (daysAgo <= 0) return 'Checked today';
  if (daysAgo === 1) return 'Checked yesterday';
  return `Checked ${daysAgo} days ago`;
}

export async function invokeHomePropertyEnrichment(
  client: {
    functions: {
      invoke: <T>(
        functionName: string,
        options: { body: Record<string, unknown> },
      ) => Promise<{ data: T | null; error: { message?: string } | null }>;
    };
  },
  address: string,
) {
  const input = normalizeHomePropertyLookupInput(address);
  if (!input.canLookup || !input.lookupQuery) {
    return {
      data: buildUnavailablePropertyEnrichment(input, input.reason || 'Add more address detail before checking property details.'),
      error: null,
    };
  }

  return client.functions.invoke<HomePropertyEnrichmentResult>('home-property-enrichment', {
    body: {
      address: input.lookupQuery,
      postcode: input.postcode,
    },
  });
}

export function buildEpcExpiryReminderDate(value: string | null | undefined, daysBefore = 30) {
  if (!value) return null;
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return null;
  return addDays(date, -daysBefore).toISOString().slice(0, 10);
}
